/**
 * DP ASOG LAB — DP101 chapter 8. Two mechanisms, opposite first actions.
 *
 * ── WHY THIS CHAPTER GETS A CLASSIFIER AND NOT A SIMULATION ───────────────────────────────────
 * Chapters 2, 3 and 6 teach quantities, and a bench can compute those. This one teaches a DECISION
 * taken under pressure, and the decision turns on a distinction whose two halves have OPPOSITE
 * first actions: take manual control, or bring the head to weather. A pair like that is learned
 * wrongly once and then acted on wrongly for a career.
 *
 * The discriminator is one question, and it is the chapter's: is thrust being APPLIED, or is it
 * ABSENT? Everything else — what failed, how fast, how close the structure is — is detail.
 *
 * ── THE SCENARIOS ARE THE CHAPTER'S DEFINITIONS, NOT INVENTED CASUALTIES ──────────────────────
 * An invented incident is written to be recognisable, so a learner who sorts four of them has
 * learned to recognise my writing. Each scenario here is a re-statement of a mechanism the chapter
 * DEFINES — including the one that catches most people: a wrong command accepted by a healthy
 * system, where nothing has broken at all.
 *
 * ── AND THE ONE NUMBER IT SHOWS IS DERIVED, NOT RECALLED ──────────────────────────────────────
 * "Come head to weather" is the standing answer to a degraded plant, and the chapter is explicit
 * that this should be derivable rather than remembered. So the drift-off action is shown beside her
 * actual post-failure envelope by heading, computed here — the reason is on screen with the rule.
 */

import { useState, useEffect } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  SCENARIOS, MECHANISMS, ASOG_STATUSES, TRIGGERS, NEAR_LOSS, HEADING_IS_THE_VARIABLE,
  ESCAPE_ROUTE, SOURCE_CH8, assemble, worstCase, threeQuestions, WHY_NOT_SCORED, loadCore,
} from './dplabs.mjs';

export default function DpAsogLab() {
  const [answers, setAnswers] = useState({});
  const [model, setModel] = useState(null);
  const [core, setCore] = useState(null);

  /* The envelope is a supporting fact here, not the subject — so if the engine is unavailable the
     classifier still works and only the derived heading note is withheld. A lab that refused
     everything because one panel could not load would be teaching nothing to make a point. */
  useEffect(() => {
    let live = true;
    Promise.all([fetch('/api/dp/model').then((r) => r.json()), loadCore()])
      .then(([m, c]) => { if (live) { setModel(m); setCore(c); } })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  let envelope = null;
  if (model && core) {
    const built = assemble(model, [...(model.thrusters || []).map((t) => t.id), ...(model.generators || []).map((g) => g.id)]);
    const w = worstCase(built, core, model).worst;
    if (w) envelope = { label: w.label, ...threeQuestions(built, core, model, w.dead, w.deadGenerators) };
  }

  const pick = (id, m) => setAnswers((a) => ({ ...a, [id]: m }));
  const mech = (id) => MECHANISMS.find((m) => m.id === id);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">ASOG · the first action, decided in daylight</div>
      <h2 className="g-h2 lab-h">Two ways to lose position. Opposite first actions.</h2>

      <p className="g-body">
        <Value of={cited('The DPO\'s authority to stop the work at the agreed trigger is written down in it. Escalation is the execution of a prior decision, not a negotiation held during the emergency.', SOURCE_CH8)} />
      </p>

      {/* ── THE TWO MECHANISMS ── */}
      <div className="lab-task dpa-mechs">
        {MECHANISMS.map((m) => (
          <div key={m.id} className={`dpa-mech dpa-${m.id}`}>
            <b>{m.name}</b>
            <p className="g-body"><Value of={cited(m.says, SOURCE_CH8)} /></p>
            <p className="g-body g-dim">{m.how} — {m.danger}.</p>
            <p className="g-body dpa-action"><b>First action:</b> <Value of={cited(m.first_action, SOURCE_CH8)} /></p>
            <p className="g-body g-dim">{m.why}</p>
          </div>
        ))}
      </div>

      {/* ── SORT THEM ── */}
      <div className="lab-task">
        <div className="g-label">Which mechanism? Ask one question: is thrust being applied, or is it absent?</div>
        {SCENARIOS.map((s) => {
          const given = answers[s.id];
          const right = given === s.answer;
          return (
            <div key={s.id} className={`dpa-case ${given ? (right ? 'clm-ok' : 'clm-miss') : ''}`}>
              <p className="g-body">{s.text}</p>
              {!given ? (
                <div className="lab-task-head">
                  {MECHANISMS.map((m) => (
                    <button key={m.id} className="g-btn" onClick={() => pick(s.id, m.id)}>{m.name}</button>
                  ))}
                </div>
              ) : (
                <>
                  <p className="g-body">
                    {right
                      ? <Value of={computed(`${mech(s.answer).name} — yes`)} />
                      : <Value of={refused(`you said ${mech(given).name}; it is ${mech(s.answer).name}`)} />}
                  </p>
                  <p className="g-body">{s.because}</p>
                  <p className="g-body dpa-action">
                    <b>So the first action is:</b> <Value of={cited(mech(s.answer).first_action, SOURCE_CH8)} />
                    {s.answer === 'drift-off' && <> — <Value of={cited(ESCAPE_ROUTE, SOURCE_CH8)} />.</>}
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── WHY HEAD TO WEATHER, COMPUTED ── */}
      <div className="lab-task dpb-verdict">
        <div className="g-label">Why “head to weather” is the standing answer — derived, not recalled</div>
        {envelope ? (
          <>
            <p className="g-body">
              After her worst case ({envelope.label}) she holds{' '}
              <Value of={computed(`${envelope.best_max_wind_kn} kn at ${envelope.best_heading_deg}°`)} /> and{' '}
              <Value of={computed(`${envelope.worst_max_wind_kn} kn at ${envelope.worst_heading_deg}°`)} />.
            </p>
            <p className="g-body">
              <Value of={cited('Post-failure capability is greatest head or stern to weather and least on the beam', SOURCE_CH8)} />
              {' '}— which is why heading control is the last thing a DPO surrenders.
            </p>
          </>
        ) : (
          <p className="g-body"><Value of={absent()} /> Her envelope is not available in this session, so the numbers behind the rule are withheld rather than approximated.</p>
        )}
        <p className="g-body g-dim">{HEADING_IS_THE_VARIABLE}</p>
      </div>

      {/* ── THE STATUSES AND TRIGGERS ── */}
      <div className="lab-task">
        <div className="g-label">The ASOG itself — agreed before the operation, by vessel, client and facility</div>
        <table className="dpb-table">
          <tbody>
            {ASOG_STATUSES.map((s) => (
              <tr key={s.id}><td><b>{s.id}</b></td><td><Value of={cited(s.says, SOURCE_CH8)} /></td></tr>
            ))}
          </tbody>
        </table>
        <div className="g-label dpa-trig">Three triggers you have already met, and where they land</div>
        {TRIGGERS.map((t) => (
          <p key={t.id} className="g-body">
            <Value of={cited(t.says, SOURCE_CH8)} /> <span className="g-dim">({t.from}) — {t.means}</span>
          </p>
        ))}
        <p className="g-body g-dim">
          Each maps to a written action. None of them is a judgement call at the time. That is the point.
        </p>
        <p className="g-body g-dim">{NEAR_LOSS}</p>
      </div>

      <p className="g-body g-dim lab-foot">{WHY_NOT_SCORED}</p>
      <p className="g-body g-dim"><Value of={absent()} /> Nothing here is an attestation, and no ASOG is a substitute for the one agreed for the actual job.</p>
    </div>
  );
}
